import React, { useState, useMemo, useCallback } from "react";
import {
  Stack,
  Box,
  Container,
  Grid,
  SegmentedControl,
  rem,
} from "@mantine/core";
import { useTranslation } from "../context/TranslationContext";
import ProductCard from "./ProductCard";
import ProductDetailImageGallery from "./ProductDetailImageGallery";
import ProductDetailInfo from "./ProductDetailInfo";
import ProductDetailPurchaseCard from "./ProductDetailPurchaseCard";
import { localizedField } from "../utils/localizedField";
import { isVariantSoldOut } from "../utils/soldOut";

const API_BASE_URL =
  process.env.REACT_APP_API_URL;

function resolveImageUrl(idOrUrl) {
  if (!idOrUrl) return null;
  if (typeof idOrUrl === "string" && idOrUrl.startsWith("http")) return idOrUrl;
  return `${API_BASE_URL}/files/${idOrUrl}`;
}

function resolveImages(imagesField) {
  let images = imagesField;
  if (typeof images === "string") {
    try { images = JSON.parse(images); } catch { images = []; }
  }
  if (!images || !Array.isArray(images)) return [];
  return images
    .map((entry) => {
      if (!entry) return null;
      if (typeof entry === "string") {
        const url = resolveImageUrl(entry);
        return url ? { url, lang: null } : null;
      }
      if (entry.id) {
        const url = resolveImageUrl(entry.id);
        return url ? { url, lang: entry.lang || null } : null;
      }
      return null;
    })
    .filter(Boolean);
}

function buildPreviewVariant(v, allImages, translations, vatRate) {
  // The form value is NET (prices are stored VAT-exclusive). The preview renders
  // the retail view, so `price`/`originalPrice` are exposed GROSS (chargeable
  // figure, net * (1 + rate/100)) with vatExclusiveDisplay off — mirroring the
  // public product endpoint. `netPrice` keeps the NET figure.
  const net = v.price != null ? Number(v.price) : null;
  const rate = Number(vatRate) || 0;
  const price = net != null ? Math.round(net * (1 + rate / 100) * 100) / 100 : null;
  const images = resolveImages(v.images);
  const parentImages = resolveImages(v.parent_images);
  const fallbackImages = images.length > 0 ? images : parentImages;
  const fallbackUrls = fallbackImages.map((img) => img.url);
  const cardImage = fallbackUrls[0] || null;

  return {
    id: v.sku || v.id || "preview",
    sku: v.sku || "preview",
    price,
    originalPrice: price,
    hasSpecialPrice: false,
    discountPercentage: null,
    customerPrice: null,
    vatRate,
    vatAmount: 0,
    vatExclusiveDisplay: false,
    netPrice: net,
    weight_value: v.weight_value != null ? Number(v.weight_value) : null,
    weight_unit: v.weight_unit || null,
    stock_quantity: v.stock_quantity != null ? Number(v.stock_quantity) : 99,
    article_number: v.article_number || "",
    image: cardImage,
    images: fallbackUrls,
    allImages: fallbackImages,
    offer: null,
    is_default: Boolean(v.is_default),
    can_preorder: v.can_preorder == null || Number(v.can_preorder) !== 0,
    business_only: Boolean(v.business_only),
    is_hidden: Boolean(v.is_hidden),
    sort_order: v.sort_order || 0,
    translations,
  };
}

export default function ProductPreview({
  formValues,
  parentImages,
  variantImages,
  variants = [],
  langCodes,
  isBulkOnly,
  isVariableWeight,
}) {
  const { t } = useTranslation();
  const [view, setView] = useState("card");
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState(null);

  const translations = useMemo(
    () =>
      langCodes.map((code) => ({
        language_code: code,
        name: formValues[`name_${code}`] || "",
        description: formValues[`description_${code}`] || "",
      })),
    [formValues, langCodes]
  );

  const vatRate = Number(formValues.vat_rate) || 2.6;

  const parentAllImages = useMemo(() => {
    const imgs = [];
    for (const img of parentImages) {
      const url = img.saved ? resolveImageUrl(img.id) : img.preview;
      if (url) imgs.push({ url, lang: img.lang || null });
    }
    return imgs;
  }, [parentImages]);

  const product = useMemo(() => {
    const productAllImages = [...parentAllImages];

    let previewVariants;
    if (variants.length > 0) {
      previewVariants = variants.map((v) => {
        const variant = buildPreviewVariant(v, productAllImages, translations, vatRate);
        if (variant.allImages.length > 0) {
          productAllImages.push(...variant.allImages);
        }
        return variant;
      });
    } else {
      const net =
        formValues.price != null && formValues.price !== ""
          ? Number(formValues.price)
          : null;
      // Full bulk product (no variants). The form value is NET; expose GROSS
      // (retail view) exactly like buildPreviewVariant does.
      const rate = Number(vatRate) || 0;
      const price = net != null ? Math.round(net * (1 + rate / 100) * 100) / 100 : null;
      const weightValue =
        formValues.weight_value != null && formValues.weight_value !== ""
          ? Number(formValues.weight_value)
          : null;

      for (const img of variantImages) {
        const url = img.saved ? resolveImageUrl(img.id) : img.preview;
        if (url) productAllImages.push({ url, lang: img.lang || null });
      }

      previewVariants = [
        {
          id: "preview-variant",
          sku: "preview-variant",
          price,
          originalPrice: price,
          hasSpecialPrice: false,
          discountPercentage: null,
          customerPrice: null,
          vatRate,
          vatAmount: 0,
          vatExclusiveDisplay: false,
          netPrice: net,
          weight_value: weightValue,
          weight_unit: formValues.weight_unit || null,
          stock_quantity: 99,
          article_number: "",
          image: productAllImages.length > 0 ? productAllImages[0].url : null,
          images: productAllImages.map((img) => img.url),
          allImages: [...productAllImages],
          offer: null,
          is_default: true,
          can_preorder: true,
          business_only: false,
          is_hidden: false,
          sort_order: 0,
          translations,
        },
      ];
    }

    const cardImage =
      productAllImages.length > 0 ? productAllImages[0].url : null;

    return {
      product_code: "preview",
      name: formValues.name_en || formValues.name_de || "",
      category: formValues.category || "",
      is_bulk_only: isBulkOnly,
      is_variable_weight: isVariableWeight,
      is_hidden: false,
      image: cardImage,
      images: productAllImages.map((img) => img.url),
      allImages: productAllImages,
      translations,
      price: previewVariants[0]?.price ?? null,
      offer: null,
      variants: previewVariants,
    };
  }, [
    formValues, parentAllImages, variantImages, variants,
    langCodes, isBulkOnly, isVariableWeight, translations, vatRate,
  ]);

  const visibleVariants = useMemo(
    () => product.variants.filter((v) => !v.is_hidden),
    [product.variants]
  );

  const selectedVariant = useMemo(() => {
    if (selectedVariantId) {
      const found = visibleVariants.find((v) => v.id === selectedVariantId);
      if (found) return found;
    }
    return visibleVariants.find((v) => v.is_default) || visibleVariants[0] || null;
  }, [visibleVariants, selectedVariantId]);

  const handleVariantChange = useCallback((v) => {
    setSelectedVariantId(v.id);
    setSelectedImageIndex(0);
  }, []);

  const displayPrice = selectedVariant?.price ?? product.price;
  const isSoldOut = !selectedVariant || isVariantSoldOut(selectedVariant);

  const displayImages = useMemo(() => {
    const currentLang = t.langCode;

    const filterByLang = (imgs) => {
      const langMatch = [];
      const common = [];
      const otherLang = [];
      for (const img of imgs) {
        const url = typeof img === "string" ? img : img?.url;
        if (!url) continue;
        if (!img.lang) {
          common.push(url);
        } else if (img.lang === currentLang) {
          langMatch.push(url);
        } else {
          otherLang.push(url);
        }
      }
      return { langMatch, common, otherLang };
    };

    const pFiltered = filterByLang(product.allImages || []);
    const vFiltered = filterByLang(selectedVariant?.allImages || []);

    const allImages = [
      ...pFiltered.common,
      ...pFiltered.langMatch,
      ...vFiltered.common,
      ...vFiltered.langMatch,
    ];

    const seen = new Set();
    return allImages.filter((url) => {
      if (seen.has(url)) return false;
      seen.add(url);
      return true;
    });
  }, [product.allImages, selectedVariant?.allImages, t.langCode]);

  const descriptionHtml = useMemo(() => {
    const desc = localizedField(product, "description", t.langCode);
    const articleHtml = selectedVariant?.article_number
      ? `<h3>${t("articleNumber", "Article Number")}</h3><p>${selectedVariant.article_number}</p>`
      : "";
    return desc + articleHtml;
  }, [product, selectedVariant, t]);

  return (
    <Stack gap="xl" mt="md" role="region" aria-label="Product preview">
      <SegmentedControl
        value={view}
        onChange={setView}
        fullWidth
        data={[
          { value: "card", label: "Card View (Listing Page)" },
          { value: "detail", label: "Detail View (Product Page)" },
        ]}
        aria-label="Preview layout"
      />

      {view === "card" ? (
        <Box maw={360} mx="auto" aria-hidden="true">
          <ProductCard product={product} previewMode />
        </Box>
      ) : (
        <Box
          pb={rem(100)}
          aria-hidden="true"
          style={{
            background: "#f9fafb",
            borderRadius: 12,
            border: "1px solid #e9ecef",
            overflow: "hidden",
          }}
        >

          <Container size="lg" pt={rem(40)}>
            <Grid gutter={rem(80)}>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <ProductDetailImageGallery
                  displayImages={displayImages}
                  selectedImageIndex={selectedImageIndex}
                  onSelectImage={setSelectedImageIndex}
                  productName={product.name}
                  isSoldOut={isSoldOut}
                  previewMode
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap="xl">
                  <ProductDetailInfo
                    product={product}
                    selectedVariant={selectedVariant}
                    productName={product.name}
                    displayPrice={displayPrice}
                    displayOriginal={null}
                    hasSpecialPrice={false}
                    vatExclusive={false}
                    discountPct={null}
                    isSoldOut={isSoldOut}
                    t={t}
                  />

                  <ProductDetailPurchaseCard
                    product={product}
                    variants={visibleVariants}
                    selectedVariant={selectedVariant}
                    quantity={1}
                    onQuantityChange={() => {}}
                    stockQuantity={selectedVariant?.stock_quantity ?? 99}
                    isSoldOut={isSoldOut}
                    onAddToCart={() => {}}
                    addingToCart={false}
                    addedToCart={false}
                    onBuyNow={() => {}}
                    onVariantChange={handleVariantChange}
                    previewMode
                    t={t}
                  />

                  <Box
                    className="product-description-content"
                    dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                  />
                </Stack>
              </Grid.Col>
            </Grid>
          </Container>
        </Box>
      )}
    </Stack>
  );
}
